import { Box, Heading, Text } from '@wix/design-system';
import {
  DEFAULT_PURCHASE_CYCLE,
  getStudioStyling,
  ProductHat,
  useAnonymous,
  useIsSeasonalSale,
  useProductHat,
  useRevShare,
} from '@wix/package-picker-shared-studio';
import {
  Price,
  ProductCardList,
} from '@wix/premium-purchase-platform/@components';
import React from 'react';
import { StudioPackagePickerModule } from '../../../modules/studio-package-picker.module';
import { SelectButton } from '../../SelectButton';
import { ProductCardFeatureRow } from './ProductCardFeatureRow';

interface ProductCardProps {
  productId: string;
}

export const ProductCard = ({ productId }: ProductCardProps) => {
  const { isAnonymous } = useAnonymous();
  useRevShare({ productId }); // Pre-fetch revshare data to prevent lags in cycles page

  const { data: product } =
    StudioPackagePickerModule.SDK.studioOfferingApiClient.hooks.useFetchProduct(
      {
        productId,
      },
    );
  const { name, description, features, isRecommended, isEnterprise, hasSale } =
    product ?? {};

  const showPrice = !isEnterprise;

  const { hasHat } = useProductHat({ productId });
  const isSeasonalSale = useIsSeasonalSale();

  const isElevated = isSeasonalSale ? isRecommended : hasHat;
  const hatHeight = '30px';

  const { colors } = getStudioStyling();

  return (
    <ProductCardList.Default.Card.Root
      width="100%"
      maxWidth="230px"
      marginLeft="-1px"
      elevation={isElevated ? 'Large' : 'None'}
      boxShadow={
        isElevated
          ? '0px 6px 6px 0px #0006240D, 0px 0px 18px 0px #0006241A' // TODO: Move elsewhere
          : undefined
      }
      borderTop={
        isElevated ? `3px solid transparent` : `3px solid ${colors.borderTop}`
      }
    >
      <ProductCardList.Default.Card.Tag isVisible={hasHat} height={hatHeight}>
        <ProductHat productId={productId} height={hatHeight} />
      </ProductCardList.Default.Card.Tag>
      <ProductCardList.Default.Card.Header
        height="60px"
        direction="vertical"
        gap="3px"
        width="90%"
        align="center"
        textAlign="center"
        marginTop={isSeasonalSale ? 'SP3' : undefined}
        marginBottom="SP1"
      >
        <Heading size="medium">{name}</Heading>
        <Text size="small">{description}</Text>
      </ProductCardList.Default.Card.Header>
      <ProductCardList.Default.Card.Price
        padding="0 SP2 SP3 SP2"
        align="center"
        direction="vertical"
        gap="3px"
        visibility={showPrice ? 'visible' : 'hidden'}
      >
        <Price
          cycle={DEFAULT_PURCHASE_CYCLE}
          productId={productId}
          display={{
            hideZeroDecimals: false,
            size: 'small',
            showFullPeriodPrice: false,
            inline: false,
            light: false,
          }}
          fullPriceSlot={
            isSeasonalSale && !hasSale ? () => <Box height="24px" /> : undefined
          }
        />
      </ProductCardList.Default.Card.Price>
      {!isAnonymous && (
        <Box width="90%" align="center">
          <SelectButton
            productId={productId}
            filled={isRecommended}
            size="medium"
            source="cards"
          />
        </Box>
      )}
      <ProductCardList.Default.Card.FeatureList
        marginTop="SP3"
        padding="0 21px SP4 21px"
        boxSizing="border-box"
        gap="SP4"
        verticalAlign="top"
        renderRow={(row) => <ProductCardFeatureRow row={row} />}
        features={features ?? []}
      />
    </ProductCardList.Default.Card.Root>
  );
};
